import * as React from "react";
import { Pencil } from "lucide-react";
import { Link } from "~/components/Link";
import { getChartEditURL } from "~/routes";
import { PageHeader } from "~/components/PageHeader";
import { Button } from "~/components/ui/button";
import { Chart } from "~/modules/charts/types";
import { ChartRenderer } from "~/modules/charts/components/ChartRenderer";
import { ChartDeleteButton } from "~/modules/charts/components/ChartDeleteButton";
import { ChartCloneButton } from "~/modules/charts/components/ChartCloneButton";
import { ChartFavoriteButton } from "~/modules/charts/components/ChartFavoriteButton";
import { VisualizationGrid } from "~/modules/visualization/components/VisualizationGrid";
import { TYPE_META, VISIBILITY_META } from "~/modules/charts/consts";
import { cn } from "~/lib/utils";
import { ImageLoader } from "~/components/ImageLoader";
import { RWA_COIN_ICON } from "~/consts";
import { TimeAgoDisplay } from "~/components/DateDisplay";
import { ChartsUserEvents } from "~/modules/analytics/events/charts";
import { useUser } from "~/modules/user/hooks/useUser";

// // // //

/** Compact views formatter, e.g. 3120 → "3.1k". (Views display hidden for now.) */
// const compactCount = new Intl.NumberFormat("en-US", {
//     notation: "compact",
//     maximumFractionDigits: 1,
// });

/** Small dot separator between metadata items. */
function MetaDot() {
    return <span className="size-1 rounded-full bg-border" aria-hidden />;
}

function ChartMeta({ chart }: { chart: Chart }) {
    const type = TYPE_META[chart.type];
    const visibility = VISIBILITY_META[chart.visibility];
    const author = chart.official
        ? "RWA.xyz Research"
        : chart.createdByUserEmail;

    return (
        <div className="mb-6 flex flex-wrap items-center gap-x-2.5 gap-y-2 text-sm text-muted-foreground">
            {/* Author · updated · views */}
            <span className="inline-flex min-w-0 items-center gap-1.5">
                {!chart.official && (
                    <span className="flex size-5 flex-none items-center justify-center rounded-full bg-primary/10 text-[9.5px] font-bold uppercase text-primary">
                        {chart.createdByUserEmail
                            ? chart.createdByUserEmail[0]
                            : "?"}
                    </span>
                )}

                {chart.official && (
                    <ImageLoader
                        src={RWA_COIN_ICON}
                        alt="RWA.xyz Research"
                        className="size-4 flex flex-none items-center justify-center rounded-full bg-primary/10"
                    />
                )}

                <span className="truncate text-muted-foreground">{author}</span>
            </span>

            <MetaDot />

            <span className="text-muted-foreground">
                Updated{" "}
                <TimeAgoDisplay
                    value={new Date(chart.updatedAt).toISOString()}
                />
            </span>

            <MetaDot />

            {/* Chart type badge */}
            <span className="inline-flex items-center gap-1.5 text-sm">
                <type.icon className="size-3.5" />
                {type.label}
            </span>

            {/* Chart visibility badge — the public/private/team distinction is
            meaningless for official charts, so hide it when official. */}
            {!chart.official && (
                <>
                    <MetaDot />

                    <span
                        className={cn(
                            "inline-flex items-center gap-1.5 text-sm",
                        )}
                    >
                        <visibility.icon className="size-3.5" />
                        {visibility.label}
                    </span>
                </>
            )}

            {/* Views display hidden for now */}
            {/* <MetaDot />
                <span>
                    {compactCount.format(chart.views)}{" "}
                    {chart.views === 1 ? "view" : "views"}
                </span> */}
        </div>
    );
}

// // // //

export function ChartIndividualPage(props: { chart: Chart }) {
    const { chart } = props;
    const { user } = useUser();

    // Editing and deleting are scoped to the user's active organization on the
    // backend, so only members of the chart's org can perform them. Shared
    // charts (public / official) are viewable by others, but they shouldn't see
    // the edit or delete controls. Clone and favorite remain available to all.
    const isOrgMember = chart.organizationID === user.organization.id;

    return (
        <div className="max-w-7xl">
            <PageHeader
                title={chart.title || chart.id}
                subtitle={chart.description || "No description provided"}
                controls={
                    <>
                        <ChartFavoriteButton
                            chart={chart}
                            addFavoriteAnalyticsLabel={
                                ChartsUserEvents.ChartIndividualClickFavoriteAdd
                            }
                            removeFavoriteAnalyticsLabel={
                                ChartsUserEvents.ChartIndividualClickFavoriteRemove
                            }
                        />
                        <ChartCloneButton chart={chart} />
                        {isOrgMember && (
                            <>
                                <Button
                                    asChild
                                    variant="primaryLight"
                                    className="gap-1.5 text-base"
                                >
                                    <Link
                                        href={getChartEditURL({
                                            slug: chart.slug,
                                        })}
                                        analyticsLabel={
                                            ChartsUserEvents.ChartIndividualClickEdit
                                        }
                                        analyticsEventProperties={{
                                            chart_slug: chart.slug,
                                            chart_type: chart.type,
                                        }}
                                    >
                                        <Pencil className="size-5" />
                                        Edit
                                    </Link>
                                </Button>
                                <ChartDeleteButton chart={chart} />
                            </>
                        )}
                    </>
                }
            />
            <ChartMeta chart={chart} />
            <VisualizationGrid>
                <ChartRenderer
                    title={chart.title || chart.id}
                    description={chart.description || ""}
                    type={chart.type}
                    config={chart.config}
                />
            </VisualizationGrid>
        </div>
    );
}
